import React from "react";

function About() {
    return (
        <div className="min-h-screen pt-20 pb-24">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#6366f1] via-[#8b5cf6] via-[#a855f7] to-[#ec4899] overflow-hidden rounded-b-3xl border-b-4 border-[#fbbf24]/60 shadow-2xl shadow-[#8b5cf6]/50">
                {/* Cosmic nebula overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/20 via-transparent to-[#f472b6]/20"></div>
                <div className="absolute inset-0 bg-gradient-to-bl from-[#fbbf24]/10 via-transparent to-[#06b6d4]/10"></div>
                
                {/* Space decorations */}
                <div className="absolute top-4 left-4 text-4xl animate-bounce-joy">💡</div>
                <div className="absolute top-8 right-8 text-3xl animate-float">✨</div>
                <div className="absolute bottom-4 left-8 text-3xl animate-wiggle">🚀</div>
                <div className="absolute bottom-8 right-4 text-4xl animate-bounce-joy" style={{ animationDelay: '1s' }}>🌟</div>
                <div className="absolute top-1/2 left-1/4 text-2xl animate-twinkle">💫</div>
                <div className="absolute top-1/3 right-1/4 text-2xl animate-float" style={{ animationDelay: '0.5s' }}>🎯</div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg animate-bounce-joy bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] bg-clip-text text-transparent">
                        About Joyloom Games
                    </h1>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#fbbf24] via-[#ec4899] to-[#3b82f6] mb-6 rounded-full shadow-lg shadow-[#8b5cf6]/60"></div>
                    <p className="text-xl text-white/95 max-w-3xl leading-relaxed">
                        Crafting Digital Experiences
                    </p>
                </div>
            </div>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                {/* Welcome Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                        <p className="text-white/90 text-lg leading-relaxed text-justify">
                            Welcome to Joyloom Games, your dedicated partner in world-class mobile application development. 
                            We are not just a development studio; we are architects of engaging digital experiences, specializing 
                            in transforming ambitious ideas into high-performing, user-centric mobile apps for Android platforms.
                        </p>
                    </div>
                </section>

                {/* Mission Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Our Mission</h2>
                        <span className="text-3xl animate-bounce-joy">🎯</span>
                    </div>
                    <div className="bg-gradient-to-br from-[#6366f1]/30 via-[#8b5cf6]/20 to-[#ec4899]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#fbbf24]/40 shadow-xl">
                        <p className="text-white/95 text-lg leading-relaxed">
                            Our mission is simple: to help businesses and entrepreneurs thrive in the mobile ecosystem. 
                            We achieve this by blending cutting-edge technology with meticulous design, ensuring every app 
                            we create is robust, scalable, and delivers genuine joy and value to its users.
                        </p>
                    </div>
                </section>

                {/* Advantages Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">The Joyloom Advantage</h2>
                        <span className="text-3xl animate-bounce-joy">⭐</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-[#3b82f6]/30 via-[#6366f1]/20 to-[#8b5cf6]/30 backdrop-blur-sm rounded-xl p-6 border-2 border-[#3b82f6]/40 shadow-xl joy-hover">
                            <div className="text-3xl mb-4">🔄</div>
                            <h3 className="text-xl font-bold text-white mb-3">Full-Cycle Development</h3>
                            <p className="text-white/90 leading-relaxed">
                                From initial concept and strategy planning to UI/UX design, rigorous testing, and successful 
                                launch on the App Store and Google Play, we manage the entire development lifecycle.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-[#ec4899]/30 via-[#f472b6]/20 to-[#fb7185]/30 backdrop-blur-sm rounded-xl p-6 border-2 border-[#ec4899]/40 shadow-xl joy-hover">
                            <div className="text-3xl mb-4">🎨</div>
                            <h3 className="text-xl font-bold text-white mb-3">User-Centric Design (UI/UX)</h3>
                            <p className="text-white/90 leading-relaxed">
                                We believe that an exceptional user experience is the heart of a great app. Our designers 
                                focus on creating intuitive, seamless, and beautiful interfaces that keep users engaged.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-[#fbbf24]/30 via-[#f59e0b]/20 to-[#d97706]/30 backdrop-blur-sm rounded-xl p-6 border-2 border-[#fbbf24]/40 shadow-xl joy-hover">
                            <div className="text-3xl mb-4">💻</div>
                            <h3 className="text-xl font-bold text-white mb-3">Technology Mastery</h3>
                            <p className="text-white/90 leading-relaxed">
                                Our expert developers are proficient in the latest native and cross-platform technologies 
                                (including Swift, Kotlin, React Native, and Flutter), ensuring your product is built with 
                                the optimal tech stack for speed and future growth.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-[#10b981]/30 via-[#059669]/20 to-[#047857]/30 backdrop-blur-sm rounded-xl p-6 border-2 border-[#10b981]/40 shadow-xl joy-hover">
                            <div className="text-3xl mb-4">🤝</div>
                            <h3 className="text-xl font-bold text-white mb-3">Partnership Approach</h3>
                            <p className="text-white/90 leading-relaxed">
                                We view our clients as partners. We emphasize clear communication, transparency, and collaboration 
                                throughout the process, making sure the final product perfectly aligns with your vision and 
                                business goals.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Beyond the Code Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Beyond the Code</h2>
                        <span className="text-3xl animate-float">🚀</span>
                    </div>
                    <div className="bg-gradient-to-br from-[#a855f7]/30 via-[#9333ea]/20 to-[#7c3aed]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#a855f7]/40 shadow-xl">
                        <p className="text-white/95 text-lg leading-relaxed mb-6">
                            At Joyloom Games, we are passionate about innovation and committed to quality. Whether you need 
                            a sophisticated enterprise tool, a groundbreaking marketplace, or the next addictive mobile game, 
                            we provide the technical expertise and creative insight to bring your project to life.
                        </p>
                        <div className="flex items-center gap-2 text-xl font-semibold text-white mt-6">
                            <span className="animate-bounce-joy">🎮</span>
                            <span>Ready to launch your next big app idea? Let's build something extraordinary together.</span>
                            <span className="animate-bounce-joy" style={{ animationDelay: '0.5s' }}>✨</span>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default About;
